import Vue from 'vue';
import { Component, Emit, Prop, Ref, Watch, Mixins, Provide } from 'vue-property-decorator';

import { Section } from '@/courses/models/section.model';
import { IDeckCardDetails } from '@/courses/interfaces/deck-library.interfaces';
import EdDeckCard from '../ed-deck-card/ed-deck-card.component';
import { MEETING_EVENTS } from '@/meetings/values/meetings.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';

import ToolService from '@/providers/tools';
import { SectionMixin } from '@/courses/mixins/section.mixin';
@Component({
  components: {
    EdDeckViewerControls: () => import(/* EdDeckViewerControls */'../ed-deck-viewer-controls/ed-deck-viewer-controls.component.vue'),
    EdDeckCard: () => import(/* EdDeckCard */'../ed-deck-card/ed-deck-card.component.vue'),
    EdDeckCardsNavigation: () => import(/* EdDeckCardsNavigation */'../ed-deck-cards-navigation/ed-deck-cards-navigation.component.vue'),
  }
})
export default class EdDeckViewerComponent extends Mixins(SectionMixin)  {
  @Prop({ default: null }) readonly deck: Section;
  @Prop({ default: () => [] }) readonly cards: Section[];
  @Prop({ default: true }) readonly canEditCard: boolean;
  @Prop({ default: 0 }) readonly cardSelected: number;
  @Prop({ default: false }) readonly editionMode: boolean;
  @Prop({ default: false }) readonly canFlipByOutsideCard: boolean;
  @Prop({ default: false }) readonly hasEditionBarVisible: boolean;

  @Ref() readonly deckCard!: EdDeckCard;
  @Ref() readonly deckViewerContainer!: HTMLElement;

  @Emit('onCardSelectedChanged')
  emitCardSelectedChanged() {
    return this.selectedCard;
  }

  selectedIndex: number = -1;
  isEditIconShown = false;
  isEditorShown = false;
  isCardsNavigationShown = false;
  cardFlipped = -1;
  removingCard = false;
  cardContainerWidth = 0;
  cardContainerHeight = 0;
  isMobile = false;

  @Provide()
  scale = Vue.observable({ value: 1 });

  get isPresentationMode() : boolean{
    return this.$store.state.CourseStore.isPresentationTeacherMode ||
      this.$store.state.CourseStore.isPresentationStudentMode;
  }

  get deckId() {
    return this.deck?.id;
  }

  get cardList(): IDeckCardDetails[] {
    return this.cards.map(c => ({
      data: c,
      hasBack: this.getHavetoShowBackCard(c),
      isSelected: this.selectedCard?.id === c.id,
      isFlipped: c.id === this.cardFlipped,
    }));
  }

  get selectedCard() {
    return this.selectedIndex >= 0 ? this.cards[this.selectedIndex] : null;
  }

  get hasBackSelectedCard(): boolean {
    return this.getHavetoShowBackCard(this.selectedCard);
  }

  get canFlip() {
    return !this.$store.state.CourseStore.isPresentationStudentMode;
  }

  getHavetoShowBackCard(card: Section): boolean {
    if (card?.text === 'DOUBLE') {
      return !!card.sections[1];
    }

    return false;
  }

  nextCard() {
    if(this.selectedIndex < this.cards.length) {
      this.deckCard.flipped = false;
      this.selectedIndex++;
    }
  }
  
  prevCard() {
    if (this.selectedIndex > 0) {
      this.deckCard.flipped = false;
      this.selectedIndex--;
    }
  }

  initViewer() {
    if (this.cards && this.cards.length > 0) {
      const indexFound = this.cards.findIndex(el => el.id === this.cardSelected);

      this.selectedIndex = indexFound !== -1 ? indexFound : 0;
    }
  }

  showCardEditor(card?: Section) {
    this.goToDeck(null, true);
    this.$Courses.$emit(COURSES_EVENTS.OPEN_DECK_EDITOR);
  }

  showCardsNavigation(isCardsNavigationShown: boolean) {
    this.isCardsNavigationShown = isCardsNavigationShown;
  }

  onCardFlipped(isFlipped: boolean) {
    this.cardFlipped = isFlipped ? this.selectedCard.id : -1;
    this.emitCardChanged(this.selectedCard, !isFlipped, this.selectedIndex);
  }

  onCardSelected({ card, isFlipped }: { card: Section, isFlipped: boolean }) {
    const index = this.cards.findIndex(c => c.id === card.id);

    if (index !== -1) {
      this.selectedIndex = index;

      setTimeout(() => {
        if (this.deckCard) {
          this.deckCard.flipped = isFlipped;
  
          this.onCardFlipped(isFlipped);
        }
      }, 100);
    }
  }

  goToFlipCard() {
    if(this.canFlip) {
      this.deckCard.toggle();
    }
  }

  onViewerResize() {
    this.cardContainerWidth =  this.deckViewerContainer.offsetWidth;
    this.cardContainerHeight =  this.deckViewerContainer.offsetHeight;
    this.resetScale();
  }

  onMouseLeaveOfCardNavigation() {
    this.showCardsNavigation(false);
  }

  zoomIn() {
    this.scale.value += 0.1;
  }

  zoomOut() {
    this.scale.value -= 0.1;
  }

  resetScale() {
    this.scale.value = 1;
  }

  created() {
    this.initViewer();
    this.emitCardChanged(this.selectedCard, true, this.selectedIndex);
    this.$edEventBus.$on(MEETING_EVENTS.RESIZE_WHITEBOARD_TRANSPARENCY, this.onViewerResize);
  }

  beforeDestroy() {
    this.$edEventBus.$off(MEETING_EVENTS.RESIZE_WHITEBOARD_TRANSPARENCY, this.onViewerResize);
  }

  mounted() {
    this.isMobile = ToolService.getIsMobileDevice();

    setTimeout(() => {
      this.onViewerResize();
    }, 100);
  }

  @Watch('selectedIndex')
  onSelectedIndexChanged() {
    setTimeout(() => {
      this.emitCardSelectedChanged();
    }, 100);
  }

  @Watch('selectedCard', { deep: true, immediate: false })
  private selectedCardChanged() {
    this.emitCardChanged(this.selectedCard, true, this.selectedIndex);
  }

  private emitCardChanged(card, isFrontSide, index) {    
    let cardData =  { card, isFrontSide, index};
    this.cardChanged(cardData);
  }

  @Emit('onCardChanged')
  private cardChanged(cardData): void {
    // Let the @Emit decorator just emit the passed data
  }
}
